import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import InviteTeammate from "@/components/InviteTeammate";
import OgPreviewCard from "@/components/OgPreviewCard";
import TrialExtensionCard from "@/components/TrialExtensionCard";
import { Hammer, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

function StatusPill({ ok, loading, testid }) {
  if (loading) {
    return (
      <span
        data-testid={testid}
        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border text-zinc-500 bg-zinc-900 border-zinc-800"
      >
        …
      </span>
    );
  }
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
        ok
          ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
          : "text-amber-400 bg-amber-500/10 border-amber-500/30"
      }`}
    >
      {ok ? <CheckCircle size={10} weight="fill" /> : <WarningCircle size={10} weight="fill" />}
      {ok ? "Configured" : "Not configured"}
    </span>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [emailStatus, setEmailStatus] = useState(null);
  const [domainStatus, setDomainStatus] = useState(null);
  const [psiStatus, setPsiStatus] = useState(null);
  const [reviewsStatus, setReviewsStatus] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [apolloStatus, setApolloStatus] = useState(null);
  const [hunterStatus, setHunterStatus] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/email/status").then((r) => setEmailStatus(r.data)).catch(() => {});
    api.get("/email/domains").then((r) => setDomainStatus(r.data)).catch(() => setDomainStatus({ error: true }));
    api.get("/pagespeed/status").then((r) => setPsiStatus(r.data)).catch(() => {});
    api.get("/reviews/status").then((r) => setReviewsStatus(r.data)).catch(() => {});
    api.get("/payments/status").then((r) => setStripeStatus(r.data)).catch(() => {});
    api.get("/apollo/status").then((r) => setApolloStatus(r.data)).catch(() => {});
    api.get("/hunter/status").then((r) => setHunterStatus(r.data)).catch(() => {});
  }, [user]);

  // Gate non-admin users — integrations are admin-only
  if (user && user.role !== "admin") {
    return <Navigate to="/profile" replace />;
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow="Settings"
        title="Agency profile"
        subtitle="ClickForge Digital Solutions configuration & integrations."
        testid="settings-header"
      />
      <div className="px-4 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5">
          <div className="font-display font-medium text-zinc-100 mb-3">Your account</div>
          <Row label="Name" value={user?.name} testid="settings-user-name" />
          <Row label="Email" value={user?.email} testid="settings-user-email" />
          <Row label="Role" value={user?.role} testid="settings-user-role" />
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 bg-orange-600 rounded-sm flex items-center justify-center">
              <Hammer size={14} weight="bold" className="text-zinc-950" />
            </div>
            <div className="font-display font-medium text-zinc-100">ClickForge Digital Solutions</div>
          </div>
          <Row label="Website" value="clickforgeds.com" testid="settings-cf-website" />
          <Row label="Contact email" value="info@clickforgeds.com" testid="settings-cf-email" />
          <Row label="Owner" value="Michael Olson" testid="settings-cf-owner" />
          <Row label="Service area" value="Midwest US — Sioux Falls, SD focus" testid="settings-cf-area" />
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 lg:col-span-2">
          <div className="font-display font-medium text-zinc-100 mb-3">Integrations</div>

          <Row label="Claude Sonnet 4.5 (AI)" value="Active · via Emergent LLM key" testid="settings-int-claude">
            <StatusPill ok={true} testid="settings-claude-pill" />
          </Row>

          <Row
            label="Resend (email sending)"
            value={emailStatus?.configured ? `Sender: ${emailStatus.sender_email}` : "Add RESEND_API_KEY to backend/.env and verify clickforgeds.com domain at resend.com/domains"}
            testid="settings-int-resend"
          >
            <StatusPill ok={!!emailStatus?.configured} loading={emailStatus == null} testid="settings-resend-pill" />
          </Row>

          <Row
            label="Resend sender domain"
            value={
              domainStatus?.sender_domain_status === "verified"
                ? `${domainStatus.sender_domain} · verified — delivers to any inbox`
                : domainStatus?.sender_domain_status === "not_added"
                ? `${domainStatus.sender_domain || "sender domain"} not added to Resend — only delivers to delivered@resend.dev until verified`
                : domainStatus?.sender_domain_status === "pending"
                ? `${domainStatus.sender_domain} · DNS pending — check records at resend.com/domains`
                : domainStatus?.error
                ? "Could not reach Resend API. Check RESEND_API_KEY."
                : "Checking…"
            }
            testid="settings-int-resend-domain"
          >
            <StatusPill
              ok={domainStatus?.sender_domain_status === "verified"}
              loading={domainStatus == null}
              testid="settings-resend-domain-pill"
            />
          </Row>

          <Row
            label="Google PageSpeed Insights"
            value={psiStatus?.configured_with_key ? "API key configured" : "Keyless fallback (rate-limited). Add GOOGLE_PAGESPEED_API_KEY for higher quota."}
            testid="settings-int-psi"
          >
            <StatusPill ok={!!psiStatus?.configured_with_key || !!psiStatus?.keyless_fallback} loading={psiStatus == null} testid="settings-psi-pill" />
          </Row>

          <Row
            label="Google Places (reviews)"
            value={reviewsStatus?.google_configured ? "API key configured" : "Add GOOGLE_PLACES_API_KEY to enable Google reviews lookup."}
            testid="settings-int-google-places"
          >
            <StatusPill ok={!!reviewsStatus?.google_configured} loading={reviewsStatus == null} testid="settings-google-places-pill" />
          </Row>

          <Row
            label="Yelp Fusion (reviews)"
            value={reviewsStatus?.yelp_configured ? "API key configured" : "Add YELP_API_KEY to enable Yelp reviews lookup."}
            testid="settings-int-yelp"
          >
            <StatusPill ok={!!reviewsStatus?.yelp_configured} loading={reviewsStatus == null} testid="settings-yelp-pill" />
          </Row>

          <Row label="LinkedIn Sales Navigator CSV" value="Import via Leads → Sales Navigator button" testid="settings-int-salesnav">
            <StatusPill ok={true} testid="settings-salesnav-pill" />
          </Row>

          <Row
            label="Apollo.io (B2B enrichment)"
            value={apolloStatus?.configured ? `Configured — but Apollo's free tier no longer includes API access. Requires paid plan ($49/mo).` : "Skipped — Apollo removed API from free tier."}
            testid="settings-int-apollo"
          >
            <StatusPill ok={false} loading={apolloStatus == null} testid="settings-apollo-pill" />
          </Row>

          <Row
            label="Hunter.io (email finder)"
            value={hunterStatus?.configured ? `Active · ${hunterStatus.free_tier}` : "Add HUNTER_API_KEY to backend/.env. Sign up free at hunter.io — 25 searches/month, real API access."}
            testid="settings-int-hunter"
          >
            <StatusPill ok={!!hunterStatus?.configured} loading={hunterStatus == null} testid="settings-hunter-pill" />
          </Row>

          <Row
            label="Stripe (paid proposals)"
            value={stripeStatus?.configured ? `Active · ${stripeStatus.mode?.toUpperCase()} mode` : "Stripe key missing from backend/.env"}
            testid="settings-int-stripe"
          >
            <StatusPill ok={!!stripeStatus?.configured} loading={stripeStatus == null} testid="settings-stripe-pill" />
          </Row>

          <Row label="Weekly scheduler" value="Mondays · 8:00 AM · America/Chicago" testid="settings-int-cron">
            <StatusPill ok={true} testid="settings-cron-pill" />
          </Row>

          <Row label="Daily Hot-Lead Digest" value="Every day · 7:00 AM · America/Chicago" testid="settings-int-digest">
            <StatusPill ok={!!emailStatus?.configured} loading={emailStatus == null} testid="settings-digest-pill" />
          </Row>

          <Row label="Resend webhook" value="Mount at /api/webhook/resend. Subscribe to email.delivered, email.opened, email.clicked, email.bounced, email.complained, email.received." testid="settings-int-resend-webhook">
            <StatusPill ok={true} testid="settings-resend-webhook-pill" />
          </Row>

          <Row label="AI rate limit" value="5 calls / 60s per user · per AI endpoint" testid="settings-int-rl">
            <StatusPill ok={true} testid="settings-rl-pill" />
          </Row>
        </div>

        <InviteTeammate />
        <OgPreviewCard />
        <TrialExtensionCard />

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 lg:col-span-2">
          <div className="font-display font-medium text-zinc-100 mb-3">How to finish wiring email & PageSpeed</div>
          <ol className="text-sm text-zinc-300 leading-relaxed space-y-2 list-decimal list-inside">
            <li>
              <strong className="text-orange-400">Resend</strong> — Sign up at{" "}
              <a className="text-orange-400 underline-offset-2 hover:underline" href="https://resend.com" target="_blank" rel="noreferrer">resend.com</a>,
              add domain <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">clickforgeds.com</code>, copy the DNS records into your DNS host, then create an API key.
              Paste it into <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">backend/.env</code> as <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">RESEND_API_KEY=re_…</code>
            </li>
            <li>
              <strong className="text-orange-400">Google PageSpeed</strong> — Get a free API key at{" "}
              <a className="text-orange-400 underline-offset-2 hover:underline" href="https://developers.google.com/speed/docs/insights/v5/get-started" target="_blank" rel="noreferrer">developers.google.com/speed/docs/insights/v5/get-started</a>.
              Paste into <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">backend/.env</code> as <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">GOOGLE_PAGESPEED_API_KEY=…</code>.
              Already works keyless on small volumes.
            </li>
            <li>
              <strong className="text-orange-400">Google Places (reviews)</strong> — In{" "}
              <a className="text-orange-400 underline-offset-2 hover:underline" href="https://console.cloud.google.com/google/maps-apis" target="_blank" rel="noreferrer">Google Cloud Console → Maps Platform</a>, enable
              <em> Places API (New)</em>, create credentials → API key, restrict it to Places API, then paste into{" "}
              <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">GOOGLE_PLACES_API_KEY=…</code>
            </li>
            <li>
              <strong className="text-orange-400">Yelp Fusion</strong> — Create a Yelp account →{" "}
              <a className="text-orange-400 underline-offset-2 hover:underline" href="https://docs.developer.yelp.com/docs/fusion-intro" target="_blank" rel="noreferrer">developer portal</a> → Manage App → API Key. Paste into{" "}
              <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">YELP_API_KEY=…</code>
            </li>
            <li>Restart the backend (<code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">sudo supervisorctl restart backend</code>) — these settings hot-load on the next request.</li>
            <li>
              <strong className="text-orange-400">Resend webhook</strong> — In Resend Dashboard → Webhooks, add an endpoint pointing to <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">{`${window.location.origin}/api/webhook/resend`}</code>, subscribe to <em>email.delivered, email.opened, email.clicked, email.bounced, email.complained</em>. Copy the signing secret into <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">RESEND_WEBHOOK_SECRET=whsec_…</code> Inbound replies require enabling Resend Receive with an MX record on a subdomain.
            </li>
          </ol>
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 lg:col-span-2" data-testid="settings-stripe-live-checklist">
          <div className="font-display font-medium text-zinc-100 mb-3">Stripe Live-Mode Rollout Checklist</div>
          <div className="text-xs text-zinc-500 mb-3 font-mono">Currently: <span className={stripeStatus?.mode === "live" ? "text-emerald-400" : "text-amber-400"}>{(stripeStatus?.mode || "test").toUpperCase()}</span> mode</div>
          <ol className="text-sm text-zinc-300 leading-relaxed space-y-2 list-decimal list-inside">
            <li>
              Activate your Stripe account at{" "}
              <a className="text-orange-400 underline-offset-2 hover:underline" href="https://dashboard.stripe.com/settings/account" target="_blank" rel="noreferrer">dashboard.stripe.com/settings/account</a>
              {" "}— add business details, bank account, and tax info. Live keys remain hidden until activation is complete.
            </li>
            <li>
              In Stripe Dashboard → <strong>Developers → API Keys</strong>, reveal the <strong>Secret key</strong> (starts with <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">sk_live_…</code>). Paste it into <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">backend/.env</code> replacing <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">STRIPE_API_KEY</code>.
            </li>
            <li>
              In Stripe Dashboard → <strong>Developers → Webhooks → Add endpoint</strong>:
              <ul className="ml-5 mt-1.5 space-y-1 text-zinc-400 text-xs font-mono list-disc">
                <li>URL: <code className="bg-zinc-950 px-1 rounded-sm text-zinc-300">{`${window.location.origin}/api/webhook/stripe`}</code></li>
                <li>Events to send: <em>checkout.session.completed, checkout.session.expired, payment_intent.payment_failed</em></li>
                <li>Copy the <strong>Signing secret</strong> (starts with <code className="bg-zinc-950 px-1 rounded-sm">whsec_…</code>) → paste into <code className="bg-zinc-950 px-1 rounded-sm">STRIPE_WEBHOOK_SECRET</code></li>
              </ul>
            </li>
            <li>
              Set <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">ENV=production</code> in <code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">backend/.env</code>. This also flips the Resend webhook to <strong>fail-closed</strong> when its secret is unset (recommended).
            </li>
            <li>
              Restart the backend (<code className="font-mono text-xs bg-zinc-950 px-1 rounded-sm">sudo supervisorctl restart backend</code>).
            </li>
            <li>
              Smoke test with a real $0.50 charge to your own card, then refund it from the Stripe dashboard. Verify the lead's stage flipped to <strong>Won</strong> and the proposal status went to <strong>paid</strong>.
            </li>
            <li>
              <strong>PCI compliance reminder:</strong> Stripe Checkout (which we use) is PCI SAQ-A — you never see card data. Just keep this in your records.
            </li>
          </ol>
        </div>
      </div>
    </AppShell>
  );
}

function Row({ label, value, testid, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-zinc-800/60 last:border-0" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 w-1/3 shrink-0">{label}</div>
      <div className="text-sm text-zinc-200 flex-1">{value || "—"}</div>
      {children}
    </div>
  );
}
